"use client";

import {forwardRef, useImperativeHandle, useState} from "react";
import {Check, AlertTriangle, X} from "lucide-react";

export type ToastOptions = {
  message?: string;
  type?: "success" | "warning";
};

export type FormSuccessToastRef = {
  show: (options?: ToastOptions) => void;
};

const FormSuccessToast = forwardRef<FormSuccessToastRef>((_, ref) => {
  const [toast, setToast] = useState<{visible: boolean; message: string; type: "success" | "warning"}>({
    visible: false,
    message: "TRANSMISSION_SUCCESSFUL",
    type: "success",
  });

  useImperativeHandle(ref, () => ({
    show(options?: ToastOptions) {
      setToast({
        visible: true,
        message: options?.message || "TRANSMISSION_SUCCESSFUL",
        type: options?.type || "success",
      });
      setTimeout(() => setToast(prev => ({...prev, visible: false})), 5000);
    },
  }));

  if (!toast.visible) return null;

  const isWarning = toast.type === "warning";

  return (
    <div className="fixed top-5 right-5 z-50 font-mono">
      <div
        className={`flex items-center gap-3 rounded-none bg-zinc-950 border-2 ${
          isWarning ? "border-amber-500" : "border-yellow-400"
        } p-4 shadow-none select-none`}
      >
        <div
          className={`flex items-center justify-center w-6 h-6 ${
            isWarning ? "bg-amber-500 text-zinc-950" : "bg-yellow-400 text-zinc-950"
          }`}
        >
          {isWarning ? <AlertTriangle className="h-4 w-4" /> : <Check className="h-4 w-4" />}
        </div>
        <span
          className={`text-xs font-bold ${
            isWarning ? "text-amber-400" : "text-yellow-400"
          } uppercase tracking-tight`}
        >
          {toast.message}
        </span>
        <button
          onClick={() => setToast(prev => ({...prev, visible: false}))}
          className={`ml-4 ${
            isWarning ? "text-zinc-500 hover:text-amber-400" : "text-zinc-500 hover:text-yellow-400"
          } transition-colors cursor-pointer`}
          aria-label="Close alert"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

FormSuccessToast.displayName = "FormSuccessToast";

export default FormSuccessToast;
